import {
  DatasetExtraFieldsSettings,
  FilterSettingsFragment,
  PresentationSettingsFragment,
} from '@warebee/frontend/data-access-api-graphql';
import { FilterPresetDataType } from '@warebee/shared/common';
import _ from 'lodash';
import { atom, selector, selectorFamily } from 'recoil';
import { SimulationDatasetTypeKey } from '../simulation/store/simulation.types';

const getKey = (postfix: string) => `warebee-presentation-settings-${postfix}`;

export const presentationSettingsDocument = atom<PresentationSettingsFragment>({
  key: getKey('document'),
  default: null,
});

export const presentationDatasetExtraFieldsSettings = selector<
  DatasetExtraFieldsSettings[]
>({
  key: getKey('dataset-extra-fields-settings'),
  get: ({ get }) => {
    return get(presentationSettingsDocument)?.datasetExtraFieldsSettings;
  },
  set: ({ set }, value: DatasetExtraFieldsSettings[]) => {
    set(presentationSettingsDocument, current => ({
      ...current,
      datasetExtraFieldsSettings: value,
    }));
  },
});

export const presentationDatasetExtraFieldsSettingsByType = selectorFamily<
  DatasetExtraFieldsSettings,
  string
>({
  key: getKey('dataset-extra-fields-settings-by-type'),
  get:
    datasetKey =>
    ({ get }) => {
      return _.find(
        get(presentationDatasetExtraFieldsSettings),
        s => s.datasetType === datasetKey,
      );
    },
  set:
    (datasetKey: SimulationDatasetTypeKey) =>
    ({ get, set }, value: DatasetExtraFieldsSettings) => {
      const otherDatasetExtraFieldsSettings = _.filter(
        get(presentationDatasetExtraFieldsSettings),
        f => f.datasetType !== datasetKey,
      );

      set(presentationDatasetExtraFieldsSettings, [
        ...otherDatasetExtraFieldsSettings,
        value,
      ]);
    },
});

export const presentationFilterSettings = selector<FilterSettingsFragment[]>({
  key: getKey('filter-settings'),
  get: ({ get }) => {
    return get(presentationSettingsDocument)?.filterSettings;
  },
  set: ({ get, set }, value: FilterSettingsFragment[]) => {
    set(presentationSettingsDocument, current => ({
      ...current,
      filterSettings: value,
    }));
  },
});

export const presentationFilterSettingsByType = selectorFamily<
  FilterSettingsFragment,
  FilterPresetDataType
>({
  key: getKey('filter-settings-by-type'),
  get:
    presetType =>
    ({ get }) => {
      return _.find(
        get(presentationFilterSettings),
        s => s.presetType === presetType,
      );
    },
  set:
    (presetType: FilterPresetDataType) =>
    ({ get, set }, value: FilterSettingsFragment) => {
      const otherDatasetExtraFieldsSettings = _.filter(
        get(presentationFilterSettings),
        f => f.presetType !== presetType,
      );

      set(presentationFilterSettings, [
        ...otherDatasetExtraFieldsSettings,
        value,
      ]);
    },
});
